import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'

// Import the same sections you used in MiniDrawer:
import BoardsNavigation from './BoardsNavigation/BoardsNavigation'
import ConnectedUsersNav from './ConnectedUsersNav/ConnectedUsersNav'

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import DataUsageIcon from '@mui/icons-material/DataUsage'
import DynamicFormIcon from '@mui/icons-material/DynamicForm'
import InsightsIcon from '@mui/icons-material/Insights'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import SearchIcon from '@mui/icons-material/Search'
import {
  ClickAwayListener,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Slide,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useEntitiesFromSearch } from 'api/search'
import { AgentChatPanel } from 'components/AgentChat'
import EntitySearch from 'components/EntitySearch/EntitySearch'
import { RoadmapIcon } from 'components/RoadmapBuilder/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { GROBxLoader, GROBxLogo } from 'theme/components'
import TedToolsLogo from 'theme/components/Logos/TedToolsLogo'
import { User } from 'types'
import { useGlobalApproverSettings } from '../../api/aws/approvers/fetchGlobalApprovers'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { INITIAL_STATE } from '../../context/Settings/types'
import { useApproverSettings } from '../../context/Settings/useApproverSettings'
import CreateMenu from '../CreateMenu/CreateMenu'
import GroboticVersionControl from '../Version'
import BoardNavButton from './BoardsNavigation/BoardNavButton'
import DevNavMenu from './DevNavigation/DevNavMenu'
import { HorizontalNavContainer } from './HorizontalNavContainer'
import './HorizontalNavSections.css'
import NotificationsNav from './NotificationNav/NotificationNav'
import ToggleThemeNav from './ToggleThemeNav/ToggleThemeNav'

const ENV_BADGE_COLORS: Record<string, string> = {
  local: '#e53935',
  localdb: '#e66967',
  staging: '#f57c00',
  laptop: '#1976d2',
  development: '#1976d2',
}

const BackendEnvBadge = () => {
  const [env, setEnv] = useState<string | null>(null)

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_API_URL || ''
    fetch(`${baseUrl}/api/env`)
      .then(r => r.json())
      .then(data => {
        console.log('data.env', data.env)
        if (data?.env && data.env !== 'production') setEnv(data.env)
      })
      .catch(() => {}) // silently ignore — production may not have this endpoint
  }, [])

  if (!env) return null

  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        fontSize: 9,
        fontWeight: 800,
        fontFamily: 'monospace',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        color: '#fff',
        bgcolor: ENV_BADGE_COLORS[env] || '#757575',
      }}
    >
      {env}
    </Box>
  )
}

import { ROLES } from 'config/roles.config'

const SETTINGS_ADMINS = ROLES.ADMIN_IDS
const TRACKER_ADMINS = [ROLES.SYSTEM_ADMIN]
const DEV_USER_ID = ROLES.SYSTEM_ADMIN

const HorizontalNavSections = ({
  handleOpenCreateOrganization,
  handleOpenCreateDeal,
  handleOpenCreateContact,
  onOpenCreateTask,
  onOpenCreateBatchDigital,
  onCloseCreateBatchDigital,
  onCloseCreateTask,
  onOpenCreativeIntake,
  onCloseCreativeIntake,
  onOpenMarketingForm,
  onCloseMarketingForm,
  isConnectedUsersOpen,
  handleOpenConnectedUsers,
  handleCloseConnectedUsers,
  // isNotifyDrawerOpen,
  // onOpenNotify,
  // onCloseNotify,
  selectedPath,
  handleNavigation,
  user,
  unreadCount,
  // devSectionIds,
  // imageUrl,
  checked,
  onChecked,
  isLoading,
}: {
  handleOpenCreateOrganization: () => void
  handleOpenCreateDeal: () => void
  handleOpenCreateContact: () => void
  onOpenCreateTask: () => void
  onOpenCreateBatchDigital: () => void
  onCloseCreateBatchDigital: () => void
  onCloseCreateTask: () => void
  onOpenCreativeIntake: () => void
  onCloseCreativeIntake: () => void
  onOpenMarketingForm: () => void
  onCloseMarketingForm: (_event: any, _reason: any) => void
  isConnectedUsersOpen: boolean
  handleOpenConnectedUsers: () => void
  handleCloseConnectedUsers: () => void
  // isNotifyDrawerOpen?: any
  // onOpenNotify?: any
  // onCloseNotify?: any
  selectedPath: string
  handleNavigation: (path: any) => void
  user: User
  unreadCount: number
  // devSectionIds: any
  // imageUrl?: any
  checked: boolean
  onChecked: () => void
  isLoading: boolean
}) => {
  const theme = useTheme()
  const { pinned, agentDock, isAgentDockOpen, setIsAgentDockOpen } = useLayoutContext()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { data: globalApprovers, isLoading: isGlobalApproversLoading } = useGlobalApproverSettings(true)
  const settings = useApproverSettings(globalApprovers ?? INITIAL_STATE)
  const { handleOpenApproversSettings, handleCloseApproversSettings, isApproverSettingsOpen } = settings
  const isSettingsAdmin = SETTINGS_ADMINS?.includes(user?.user_uuid)
  const isTrackerAdmin = TRACKER_ADMINS?.includes(user?.user_uuid)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isAgentChatOpen, setIsAgentChatOpen] = useState(false)
  const [isAgentChatPoppedOut, setIsAgentChatPoppedOut] = useState(
    () => localStorage.getItem('agentChat_poppedOut') === 'true'
  )
  const { isItemsLoading } = useEntitiesFromSearch()
  const location = useLocation()
  const navigate = useNavigate()
  const isRoadmap = 'roadmap' === selectedPath.replace(/^\//, '')
  // Listen for pop-out/pop-in/navigate signals via BroadcastChannel
  useEffect(() => {
    const bc = new BroadcastChannel('agent-chat-sync')
    bc.onmessage = (e: MessageEvent) => {
      if (e.data.type === 'POP_OUT') {
        setIsAgentChatPoppedOut(true)
        setIsAgentChatOpen(false)
      }
      if (e.data.type === 'POP_IN') {
        setIsAgentChatPoppedOut(false)
        setIsAgentChatOpen(true)
      }
      if (e.data.type === 'NAVIGATE') {
        navigate(e.data.pathname)
      }
    }
    return () => bc.close()
  }, [navigate])

  // Listen for undock → open floating panel
  useEffect(() => {
    const onUndock = () => setIsAgentChatOpen(true)
    window.addEventListener('agent-undock', onUndock)
    return () => window.removeEventListener('agent-undock', onUndock)
  }, [])

  // Fallback: detect pop-out window closing via storage event
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'agentChat_poppedOut') {
        setIsAgentChatPoppedOut(e.newValue === 'true')
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Broadcast route changes to the pop-out window
  useEffect(() => {
    if (!isAgentChatPoppedOut) return
    const bc = new BroadcastChannel('agent-chat-sync')
    bc.postMessage({ type: 'ROUTE_CHANGE', pathname: location.pathname })
    bc.close()
  }, [location.pathname, isAgentChatPoppedOut])
  const handleOpenEntitySearch = () => setIsSearchOpen(true)
  const handleCloseEntitySearch = () => setIsSearchOpen(false)
  const handleNavRoadmap = () => navigate('/roadmap')

  // TED Tools dropdown menu
  const [tedMenuAnchor, setTedMenuAnchor] = useState<null | HTMLElement>(null)
  const isTedMenuOpen = Boolean(tedMenuAnchor)
  const handleOpenTedMenu = (e: React.MouseEvent<HTMLElement>) => setTedMenuAnchor(e.currentTarget)
  const handleCloseTedMenu = () => setTedMenuAnchor(null)
  const handleTedNav = (path: string) => {
    navigate(path)
    handleCloseTedMenu()
  }
  const isTedToolsActive = [
    '/strategy-builder',
    '/client-audit',
    '/points-dashboard',
    '/form-builder',
    '/service-menu',
    '/kpi-dashboard',
  ].includes(location.pathname)

  useEffect(() => {
    const handleKeyDown = e => {
      // Close on Escape
      if (e.key === 'Escape') {
        setIsSearchOpen(false)
      }

      // Open on Ctrl + Shift + F
      if (e.key.toLowerCase() === 'f' && e.ctrlKey && e.shiftKey) {
        e.preventDefault() // Prevent browser's native find
        setIsSearchOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    setIsSearchOpen(false)
  }, [location.pathname])
  const testLoading = false
  return (
    <>
      <HorizontalNavContainer theme={theme} isloading={isLoading} pinned={pinned}>
        {/* Left Box: Logo on the far left */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Stack direction="row" alignItems="center" gap={0.75} pl={2}>
            {isLoading || isItemsLoading ? <GROBxLoader size={22} /> : <GROBxLogo size={22} />}
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>GROBx</Typography>
            <GroboticVersionControl />
            <BackendEnvBadge />
          </Stack>
        </Box>
        {isLoading || testLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'flex 0.3s ease',
              gap: 4,
              flex: 1, // allows this section to grow and center the items
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 4 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Divider orientation="vertical" flexItem />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Divider orientation="vertical" flexItem />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="circular" width={16} height={16} />
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'flex 0.3s ease',
                gap: 1,
                flex: 1, // allows this section to grow and center the items
              }}
            >
              {!isSmallScreen && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <BoardsNavigation
                    handleNavigation={handleNavigation}
                    selectedPath={selectedPath}
                    isLoading={isLoading}
                    horizontal
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
              {isSmallScreen && <BoardNavButton />}

              {/* Connected Users */}

              <Tooltip title={isAgentChatPoppedOut ? 'Focus AI Agent window' : 'AI Agent'}>
                <IconButton
                  onClick={() => {
                    if (isAgentChatPoppedOut) {
                      // Focus the existing pop-out window
                      window.open('', 'grobotic-agent-chat')
                    } else if (agentDock) {
                      // Toggle docked panel visibility
                      setIsAgentDockOpen(!isAgentDockOpen)
                    } else {
                      setIsAgentChatOpen(prev => !prev)
                    }
                  }}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    ...((isAgentChatOpen || isAgentChatPoppedOut || isAgentDockOpen) && {
                      boxShadow: `0 0 12px 1px ${theme.palette.text.highlight}75`,
                    }),
                    '&:hover': {
                      transform: 'scale(1.15)',
                      boxShadow: `0 0 10px 2px ${theme.palette.text.highlight}30`,
                    },
                  }}
                >
                  <SvgIcon
                    sx={{
                      fontSize: 22,
                      color:
                        isAgentChatOpen || isAgentChatPoppedOut || isAgentDockOpen ? 'text.highlight' : 'text.primary',
                      flexShrink: 0,
                    }}
                    viewBox="0 0 92.2 65"
                  >
                    <path d="M66.5,0H52.4l25.7,65h14.1L66.5,0z M25.7,0L0,65h14.4l5.3-13.6h26.9L51.8,65h14.4L40.5,0C40.5,0,25.7,0,25.7,0z M24.3,39.3l8.8-22.8l8.8,22.8H24.3z" />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
              <Tooltip title={'Roadmap Builder'}>
                <IconButton
                  onClick={handleNavRoadmap}
                  sx={{
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      transform: 'scale(1.2)',
                      '& .MuiSvgIcon-root': {
                        color: 'text.revise',
                      },
                    },
                  }}
                >
                  <RoadmapIcon sx={{ fontSize: 20, color: isRoadmap ? 'text.revise' : 'text.primary' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="TED Tools">
                <IconButton
                  onClick={handleOpenTedMenu}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    ...((isTedToolsActive || isTedMenuOpen) && {
                      backgroundColor: 'transparent',
                      filter: `grayscale(0%)`,
                    }),
                    '&:hover': {
                      backgroundColor: 'transparent',
                      transform: 'scale(1.15)',
                      filter: `grayscale(0%)`,
                    },
                  }}
                >
                  <TedToolsLogo size={20} isActive={isTedToolsActive || isTedMenuOpen} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={tedMenuAnchor}
                open={isTedMenuOpen}
                onClose={handleCloseTedMenu}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      '& .MuiMenuItem-root': {
                        py: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      },
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleTedNav('/strategy-builder')}
                  selected={location.pathname === '/strategy-builder'}
                >
                  <ListItemIcon>
                    <AutoFixHighIcon sx={{ fontSize: 18, color: '#FFB703' }} />
                  </ListItemIcon>
                  <ListItemText>Strategy Builder</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleTedNav('/service-menu')}
                  selected={location.pathname === '/service-menu'}
                >
                  <ListItemIcon>
                    <MenuBookIcon sx={{ fontSize: 18, color: 'rgb(255, 183, 3)' }} />
                  </ListItemIcon>
                  <ListItemText>Service Menu</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleTedNav('/points-dashboard')}
                  selected={location.pathname === '/points-dashboard'}
                >
                  <ListItemIcon>
                    <DataUsageIcon sx={{ fontSize: 18, color: 'rgb(102, 187, 106)' }} />
                  </ListItemIcon>
                  <ListItemText>Points Dashboard</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleTedNav('/client-audit')}
                  selected={location.pathname === '/client-audit'}
                >
                  <ListItemIcon>
                    <FactCheckIcon sx={{ fontSize: 18, color: 'rgb(66, 165, 245)' }} />
                  </ListItemIcon>
                  <ListItemText>Client Audit</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleTedNav('/form-builder')}
                  selected={location.pathname === '/form-builder'}
                >
                  <ListItemIcon>
                    <DynamicFormIcon sx={{ fontSize: 18, color: 'rgb(171, 71, 188)' }} />
                  </ListItemIcon>
                  <ListItemText>Form Builder</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => handleTedNav('/kpi-dashboard')}
                  selected={location.pathname === '/kpi-dashboard'}
                >
                  <ListItemIcon>
                    <InsightsIcon sx={{ fontSize: 18, color: 'rgb(255, 167, 38)' }} />
                  </ListItemIcon>
                  <ListItemText>Performance</ListItemText>
                </MenuItem>
              </Menu>
              <CreateMenu
                onOpenCreateOrganization={handleOpenCreateOrganization}
                onOpenCreateDeal={handleOpenCreateDeal}
                onOpenCreateContact={handleOpenCreateContact}
                onOpenCreateBatchDigital={onOpenCreateBatchDigital}
                isLoading={isLoading}
              />

              <IconButton
                onClick={handleOpenEntitySearch}
                sx={{
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    transform: 'scale(1.2)',
                    '& .MuiSvgIcon-root': {
                      color: 'text.dirty',
                    },
                  },
                }}
              >
                <Tooltip
                  title={
                    <Stack>
                      <div>Search Boards</div>
                      <div>Ctrl + Shift + F to open</div>
                    </Stack>
                  }
                >
                  <SearchIcon
                    sx={{
                      height: 22,
                      width: 22,
                      color: 'text.primary',
                    }}
                  />
                </Tooltip>
              </IconButton>
              <Divider orientation="vertical" flexItem />

              <ConnectedUsersNav
                isConnectedUsersOpen={isConnectedUsersOpen}
                handleOpenConnectedUsers={handleOpenConnectedUsers}
                handleCloseConnectedUsers={handleCloseConnectedUsers}
              />
              {user && !isLoading && <NotificationsNav user={user} unreadCount={unreadCount} isLoading={isLoading} />}
              <ToggleThemeNav />
            </Box>

            <Box
              sx={{
                position: 'fixed',
                top: 11,
                right: '6%',
                transform: 'translateX(-70%)',
                padding: '8px 16px',
                borderRadius: 2,
                zIndex: 1402, // VERY important
                animation: 'flowText 8s linear infinite',
              }}
            >
              {/* {isLoading && <CircularProgress size={22} sx={{ color: 'background.loading' }} />} */}
            </Box>
            <DevNavMenu />

            {!isSmallScreen && <BoardNavButton />}
            {/* Right Box: Checkbox on the far right */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={checked ? 'Locked (Click to unlock)' : 'Unlocked (Click to lock)'} arrow>
                <IconButton onClick={onChecked} sx={{ mr: 2 }}>
                  {checked ? (
                    <LockIcon sx={{ height: 18, width: 18, color: theme.palette.text.secondary }} />
                  ) : (
                    <LockOpenIcon sx={{ height: 18, width: 18, color: theme.palette.text.secondary }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
        {/* Middle Box: All the nav items, centered */}

        {/* <EntitySearchModal isSearchOpen={isSearchOpen} onClose={handleCloseEntitySearch} />
         */}
        {isSearchOpen && (
          <ClickAwayListener onClickAway={handleCloseEntitySearch}>
            <Slide in={isSearchOpen} direction="down" mountOnEnter unmountOnExit>
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  width: '35%',
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  zIndex: 1300, // above most content but below modals
                  boxShadow: 3,
                  px: 4,
                  py: 2,
                }}
              >
                <EntitySearch onClose={handleCloseEntitySearch} />
              </Box>
            </Slide>
          </ClickAwayListener>
        )}
      </HorizontalNavContainer>
      {!isAgentChatPoppedOut && !agentDock && (
        <AgentChatPanel open={isAgentChatOpen} onClose={() => setIsAgentChatOpen(false)} />
      )}
    </>
  )
}

export default React.memo(HorizontalNavSections)
